import useRisksmartUser from '@risksmart-app/components/src/hooks/useRisksmartUser';
import type { RiskScoringModelValue } from '@risksmart-app/domain/src/types/consts/risk-scoring-model';
import { RiskScoringModel } from '@risksmart-app/domain/src/types/consts/risk-scoring-model';
import type { RoundingMode } from '@risksmart-app/domain/src/types/risk-score-breakdown';
import { roundingModeSchema } from '@risksmart-app/domain/src/types/risk-score-breakdown';
import { useGetAggregationSettingsForOrg } from 'src/hooks/queries/aggregation/useGetAggregationSettingsForOrg';

/**
 * The models whose settings panel offers a score rounding mode. On any other
 * model a `roundingMode` left behind in the config is inert on the backend, so
 * it must not stand heatmaps down either.
 */
const modelsWithScoreRounding: RiskScoringModelValue[] = [
  RiskScoringModel.TypedControlEffectivenessAverages,
  RiskScoringModel.RiskAggregationOnly,
];

/**
 * Whether the organisation keeps fractional impact and likelihood scores
 * ("Don't round"), which a matrix-cell heatmap cannot plot.
 */
export const isContinuousScoring = (
  riskModel: RiskScoringModelValue,
  roundingMode: RoundingMode | undefined
): boolean =>
  roundingMode === 'none' && modelsWithScoreRounding.includes(riskModel);

/**
 * Live read of `isContinuousScoring` for the current organisation. Reads the
 * same query as `useAggregation`; the module settings save invalidates it, so
 * a mode change shows on the next dashboard visit without a sign-out.
 */
export const useContinuousScoring = (): boolean => {
  const { isAuthenticated } = useRisksmartUser();
  const { data } = useGetAggregationSettingsForOrg({
    queryArgs: {},
    shouldSkip: !isAuthenticated,
  });
  const settings = data?.aggregation_org?.[0];
  const roundingMode = roundingModeSchema.safeParse(
    settings?.Config?.roundingMode
  );

  return isContinuousScoring(
    settings?.RiskScoringModel ?? RiskScoringModel.Default,
    roundingMode.success ? roundingMode.data : undefined
  );
};
